import React from "react";
import { TrendingUp, ArrowUp, ArrowDown, Minus } from "lucide-react";

// Tiny SVG sparkline component. Renders one polyline per essay's avg over cycles.
function Sparkline({ points, color = "indigo" }) {
  if (!points || points.length === 0) return null;
  const w = 220, h = 56, pad = 6;
  const minY = 1, maxY = 4;
  const stepX = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const yFor = (v) => h - pad - ((v - minY) / (maxY - minY)) * (h - pad * 2);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${pad + i * stepX} ${yFor(p)}`).join(" ");
  const stroke = { indigo: "#4f46e5", rose: "#f43f5e", emerald: "#10b981", amber: "#f59e0b" }[color] || "#4f46e5";
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      {/* gridlines */}
      {[1, 2, 3, 4].map((g) => (
        <line key={g} x1={pad} x2={w - pad} y1={yFor(g)} y2={yFor(g)} stroke="#e2e8f0" strokeDasharray="2 3" />
      ))}
      <path d={path} fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={i} cx={pad + i * stepX} cy={yFor(p)} r="3" fill={stroke} />
      ))}
    </svg>
  );
}

const PRETTY = {
  clarity: "Clarity",
  evidence: "Evidence",
  sentence_combining: "Sentence Combining",
  elaboration: "Elaboration",
  transitions: "Transitions",
  organization: "Organization",
  voice: "Voice",
  audience_awareness: "Audience Awareness",
  sentence_fluency: "Sentence Fluency",
  rhetorical_impact: "Rhetorical Impact",
  analytical_depth: "Analytical Depth",
  mla_formatting: "MLA Formatting",
};

export default function RevisionCycles({ essays, totalRevisions }) {
  if (!essays || essays.length === 0) {
    return (
      <div data-testid="cycles-empty" className="tactile-card p-6 text-center text-slate-500">
        <TrendingUp size={20} className="mx-auto mb-2 text-slate-400" />
        No revision cycles yet. Run an analysis with an <strong>essay title</strong> and revise to start tracking growth.
      </div>
    );
  }

  return (
    <div data-testid="cycles-section" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold flex items-center gap-2" style={{fontFamily:'Outfit'}}>
          <TrendingUp size={20} className="text-indigo-600"/> Revision Cycles
        </h2>
        <span data-testid="cycles-total" className="text-sm text-slate-500">
          {totalRevisions} total revision{totalRevisions === 1 ? "" : "s"} across {essays.length} essay{essays.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {essays.map((e, idx) => {
          const improved = e.improvement > 0;
          const same = e.improvement === 0;
          const Icon = improved ? ArrowUp : same ? Minus : ArrowDown;
          const tone = improved ? "text-emerald-700 bg-emerald-50 border-emerald-100"
            : same ? "text-slate-700 bg-slate-50 border-slate-100"
            : "text-rose-700 bg-rose-50 border-rose-100";
          const points = e.cycles.map(c => c.avg);
          const color = improved ? "emerald" : same ? "indigo" : "rose";

          // Collect dimensions seen across all cycles for per-dimension trend
          const dimNames = Array.from(new Set(e.cycles.flatMap(c => Object.keys(c.scores))));

          return (
            <div key={idx} data-testid={`cycle-card-${idx}`} className="tactile-card p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-lg truncate" style={{fontFamily:'Outfit'}}>{e.essay_title}</h3>
                <span className="text-xs rounded-full bg-slate-100 text-slate-700 px-2.5 py-1 font-bold uppercase tracking-wider">
                  {e.cycle_count} draft{e.cycle_count === 1 ? "" : "s"}
                </span>
              </div>
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>{e.last_avg.toFixed(1)}</span>
                <span className="text-sm text-slate-500">avg /4</span>
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-bold ${tone}`}>
                  <Icon size={12}/>
                  {improved ? "+" : ""}{e.improvement.toFixed(2)} vs first draft
                </span>
              </div>

              <Sparkline points={points} color={color} />

              {e.cycle_count >= 2 && dimNames.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {dimNames.slice(0, 6).map(dn => {
                    const first = e.cycles.find(c => dn in c.scores)?.scores?.[dn];
                    const last = [...e.cycles].reverse().find(c => dn in c.scores)?.scores?.[dn];
                    if (typeof first !== "number" || typeof last !== "number") return null;
                    const diff = last - first;
                    const arrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "•";
                    const cls = diff > 0 ? "text-emerald-700" : diff < 0 ? "text-rose-700" : "text-slate-500";
                    return (
                      <div key={dn} className="flex justify-between gap-2">
                        <span className="text-slate-600 truncate">{PRETTY[dn] || dn}</span>
                        <span className={`font-bold ${cls}`}>{first}→{last} <span>{arrow}</span></span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
